import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GraduationCap, Maximize2, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLang } from '@/i18n/LanguageContext';

import { images2024, images2025, images2026 } from '@/data/galleryData';

const GRADUATES_BY_YEAR = {
  2026: images2026.map((img, i) => ({ id: `2026-${i + 1}`, image: img })),
  2025: images2025.map((img, i) => ({ id: `2025-${i + 1}`, image: img })),
  2024: images2024.map((img, i) => ({ id: `2024-${i + 1}`, image: img })),
};

const GraduatesGallery = () => {
  const { t } = useLang();
  // El estado inicial será el año más reciente (el primero en las llaves ordenadas)
  const years = Object.keys(GRADUATES_BY_YEAR).sort((a, b) => b - a);
  const [activeYear, setActiveYear] = useState(years[0]);
  const [selectedImage, setSelectedImage] = useState(null);
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -400 : 400;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <section className="py-12 sm:py-16 md:py-24 bg-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-1/2 h-full bg-slate-50/50 -z-10" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-row items-start justify-between mb-6 sm:mb-8 gap-4">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-xs font-semibold text-blue-800 uppercase tracking-wider mb-4 sm:mb-5">
              <GraduationCap className="w-4 h-4 text-emerald-600" />
              {t.graduates.kicker}
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-slate-900 tracking-tight mb-3 sm:mb-5 leading-tight">
              {t.graduates.title}
            </h2>
            <p className="text-base sm:text-lg text-slate-600">
              {t.graduates.subtitle}
            </p>
          </div>
          
          <div className="flex gap-2 sm:gap-3 mt-1 shrink-0">
            <button 
              onClick={() => scroll('left')}
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-brand-primary hover:text-white hover:border-brand-primary transition-all"
            >
              <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
            <button 
              onClick={() => scroll('right')}
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-brand-primary hover:text-white hover:border-brand-primary transition-all"
            >
              <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
        </div>

        {/* Pestañas (Tabs) por Año */}
        <div className="flex gap-2 md:gap-4 mb-8 sm:mb-10 overflow-x-auto pb-2 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {years.map((year) => (
            <button
              key={year}
              onClick={() => setActiveYear(year)}
              className={`px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-sm sm:text-lg font-semibold whitespace-nowrap transition-all duration-300 ${
                activeYear === year 
                  ? 'bg-brand-primary text-white shadow-md' 
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {t.graduates?.classOf || 'Class of'} {year}
            </button>
          ))}
        </div>

        {/* Carrusel de Graduados */}
        <div className="w-full relative max-w-[1400px] mx-auto -mx-4 sm:mx-0 px-4 sm:px-0">
          <div 
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-12 pt-4 [&::-webkit-scrollbar]:hidden"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <AnimatePresence mode="wait">
              {(GRADUATES_BY_YEAR[activeYear] || []).map((grad, i) => (
                <motion.div
                  key={grad.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3, delay: i * 0.05 }}
                  onClick={() => setSelectedImage(grad.image)}
                  className="relative w-[85vw] sm:w-[320px] aspect-[4/5] shrink-0 snap-center rounded-[2rem] overflow-hidden group cursor-pointer bg-slate-100 shadow-sm hover:shadow-xl transition-all duration-500"
                >
                  <img 
                    src={grad.image} 
                    alt={`Graduate ${grad.id}`} 
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  {/* Overlay gradiente para el icono */}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  {/* Icono de expandir */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-white/20 backdrop-blur-md w-12 h-12 rounded-full flex items-center justify-center translate-y-4 group-hover:translate-y-0 transition-all duration-300">
                      <Maximize2 className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Lightbox Modal para ver la foto en grande */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/90 backdrop-blur-sm p-4"
          >
            <button 
              onClick={() => setSelectedImage(null)}
              className="absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              src={selectedImage}
              alt="Graduate Enlarged"
              className="max-w-full max-h-[90vh] object-contain rounded-xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default GraduatesGallery;
